/**
 * Belegnummern in Aufgaben sind Verweise (Benjamin, 04.10.2026): „Angebot
 * AG0237 nachfassen" — ein Tipp auf AG0237 öffnet den Beleg als Blatt über der
 * Aufgabe, mit Dokument, Status, Versand und „… ändern" (`BelegBlatt`, dasselbe
 * wie auf der Rechnungs- und der Projektseite). Schließen führt zurück zur
 * Aufgabe, an dieselbe Stelle.
 *
 * Setzt man dort den Status (Angebot angenommen, Rechnung bezahlt), gleicht der
 * Server die Phasenaufgaben sofort ab (`kontor_phasenaufgaben.gleich_abgleichen`)
 * — „nachfassen" ist dann weg, ohne dass man sie abhaken muss.
 */
import { Fragment, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { registerZeilen, useInvoiceRegister } from "../../hooks/useInvoices";
import BelegBlatt from "../projekt/BelegBlatt";
import "../../styles/invoices.css";

/** AG0237, AN0231, RE0186, GS0012, AB0004 — Kürzel und mindestens vier Ziffern. */
const NUMMER = /\b((?:AG|AN|RE|GS|AB|SR|AR)\d{4,})\b/g;

export function belegNummern(text: string): string[] {
  return Array.from(new Set(Array.from((text || "").matchAll(NUMMER), (m) => m[1])));
}

/** Text mit anklickbaren Belegnummern. */
export function MitBelegVerweisen({ text }: { text: string }) {
  const [offen, setOffen] = useState("");
  const teile: ReactNode[] = [];
  let rest = 0;
  for (const m of (text || "").matchAll(NUMMER)) {
    const i = m.index ?? 0;
    if (i > rest) teile.push(text.slice(rest, i));
    teile.push(
      <button key={i} type="button" className="nz-beleg" title={`${m[1]} öffnen`}
        onClick={(e) => { e.stopPropagation(); setOffen(m[1]); }}>
        {m[1]}
      </button>,
    );
    rest = i + m[1].length;
  }
  if (rest < (text || "").length) teile.push(text.slice(rest));
  return (
    <>
      {teile.map((t, i) => <Fragment key={i}>{t}</Fragment>)}
      {offen && <BelegNachNummer nummer={offen} onClose={() => setOffen("")} />}
    </>
  );
}

/** Den Beleg zur Nummer holen und als Blatt zeigen. */
function BelegNachNummer({ nummer, onClose }: { nummer: string; onClose: () => void }) {
  const qc = useQueryClient();
  const suche = useInvoiceRegister({ q: nummer });
  const gefunden = registerZeilen(suche.data).find((b) => b.nummer.toUpperCase() === nummer.toUpperCase());
  const [stand, setStand] = useState<typeof gefunden>(undefined);
  const beleg = stand || gefunden;

  if (suche.isLoading) return <div className="nz-beleg-laden" role="status">{nummer} wird geöffnet …</div>;
  if (!beleg) {
    return (
      <div className="nz-beleg-laden" role="alert">
        {nummer} nicht gefunden. <button type="button" className="nz-still" onClick={onClose}>Schließen</button>
      </div>
    );
  }
  return (
    <BelegBlatt
      beleg={beleg} onClose={onClose}
      onUpdated={(b) => {
        setStand(b);
        // Der Server hat die Phasenaufgaben schon abgeglichen — die Liste holt sich den neuen Stand.
        qc.invalidateQueries({ queryKey: ["notizen"] });
        qc.invalidateQueries({ queryKey: ["invoices"] });
      }}
    />
  );
}
